import { useState, useEffect, useMemo, type FormEvent } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, User, Check, Globe } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import { ApiError } from '../lib/api';
import { Navbar } from '../components/Navbar';
import { getTimezones, detectTimezone, timezoneLabel } from '../lib/timezones';
import { trackSignup } from '../lib/analytics';

const FEATURES = [
  'Publica en Instagram, X y LinkedIn',
  'Calendario visual',
  'Programación multicuenta',
  'IA para ayudarte con el contenido',
  'Vista previa del feed',
  'Colaboración en equipo',
  'Y mucho más',
];

export function Register() {
  const { register, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const oauthError = searchParams.get('error');
  // `?next=` lets team-invite /join links bring the user back after sign-up.
  const next = searchParams.get('next') || '/dashboard';

  const [name, setName]           = useState('');
  const [email, setEmail]         = useState('');
  const [password, setPassword]   = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [timezone, setTimezone]   = useState<string>(() => detectTimezone());
  const [error, setError]         = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const timezoneOptions = useMemo(
    () => getTimezones().map((tz) => ({ value: tz, label: timezoneLabel(tz) })),
    [],
  );

  useEffect(() => {
    if (user) { navigate(next, { replace: true }); return; }
    if (oauthError) {
      setError(
        oauthError === 'google_denied'
          ? 'Cancelaste el acceso con Google.'
          : 'Error al iniciar sesión con Google. Inténtalo de nuevo.',
      );
    }
  }, [oauthError, user, navigate]);

  /* password rules */
  const hasLength    = password.length >= 8;
  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const passwordValid = hasLength && hasUppercase && hasLowercase;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!passwordValid) return;
    setError(null);
    setSubmitting(true);
    try {
      const u = await register(email, password, timezone, name.trim() || undefined);
      // GA sign_up + TikTok/Meta CompleteRegistration. eventId dedupes with the
      // server CAPI event (`reg_<userId>`).
      trackSignup('email', `reg_${u.id}`);
      navigate(next, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'EMAIL_TAKEN')       setError('Ese email ya está registrado.');
      else if (err instanceof ApiError && err.code === 'VALIDATION_ERROR') setError('Revisa los datos introducidos.');
      else if (err instanceof ApiError && err.code === 'RATE_LIMITED') setError('Demasiados intentos. Espera unos minutos.');
      else setError('Algo ha fallado. Inténtalo de nuevo.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundImage: 'url(/bg-login.png)', backgroundSize: 'cover', backgroundPosition: 'center' }}
    >
      <Navbar />
      <div className="flex-1 lg:grid lg:grid-cols-2">
      {/* ── Left: marketing ─────────────────────────────── */}
      <div className="hidden lg:flex flex-col justify-center pl-12 pr-4 xl:pl-16 xl:pr-6 relative py-12">

        {/* dot grid decoration */}
        <div className="absolute top-8 left-8 grid grid-cols-6 gap-[7px] opacity-25 pointer-events-none">
          {Array.from({ length: 36 }).map((_, i) => (
            <div key={i} className="h-1.5 w-1.5 rounded-full bg-brand-500" />
          ))}
        </div>

        {/* content card */}
        <div className="bg-white/70 backdrop-blur-md rounded-3xl border border-white/80 shadow-sm px-10 py-10">

        {/* badge */}
        <div className="inline-flex items-center gap-2 bg-white/80 border border-slate-200/80 rounded-full px-4 py-1.5 w-fit mb-6 shadow-sm">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-xs font-medium text-slate-600">Prueba 3 días gratis · cancela cuando quieras</span>
        </div>

        <h1 className="text-3xl xl:text-4xl font-extrabold text-slate-900 leading-snug">
          Empieza a programar<br />contenido en minutos
        </h1>
        <p className="mt-3 text-sm text-slate-500 leading-relaxed max-w-xs">
          Crea tu cuenta y gestiona todas tus redes desde un solo lugar.
        </p>

        <ul className="mt-7 space-y-2.5">
          {FEATURES.map((f) => (
            <li key={f} className="flex items-center gap-3 text-slate-700 text-sm">
              <span className="flex items-center justify-center h-5 w-5 rounded-full bg-emerald-100 shrink-0">
                <Check size={11} className="text-emerald-600" strokeWidth={3} />
              </span>
              {f}
            </li>
          ))}
        </ul>

        <div className="mt-8 pt-7 border-t border-white/50 flex items-center justify-between max-w-sm">
          <p className="text-xs text-slate-400 leading-relaxed">
            Accede a todas las funciones<br />durante tu prueba gratuita.
          </p>
          <a href="#" className="text-xs text-brand-600 font-semibold hover:underline whitespace-nowrap ml-4">
            Ver planes →
          </a>
        </div>
        </div>{/* end content card */}
      </div>

      {/* ── Right: form card ────────────────────────────── */}
      <div className="flex items-center justify-center px-4 py-12 lg:pl-4 lg:pr-12 xl:pl-6 xl:pr-16">
        <div className="w-full max-w-lg bg-white rounded-3xl shadow-xl shadow-blue-100/50 border border-slate-100 px-12 py-10">

          {/* logo + heading */}
          <div className="flex flex-col items-center gap-2 mb-7">
            <img src="/zettapost-logo-min.png" alt="Zettapost" className="h-10 w-auto mb-1" />
            <h2 className="text-2xl font-bold text-slate-900">Crea tu cuenta</h2>
            <p className="text-sm text-slate-400">Empieza gratis en segundos</p>
          </div>

          {/* Google button */}
          <button
            type="button"
            onClick={() => { window.location.href = '/api/auth/google'; }}
            className="w-full flex items-center justify-center gap-3 border border-slate-200 rounded-xl py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 transition mb-5"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908C16.658 14.072 17.64 11.837 17.64 9.2z" fill="#4285F4"/>
              <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
              <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
              <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
            </svg>
            Continuar con Google
          </button>

          {/* divider */}
          <div className="flex items-center gap-3 mb-5">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs text-slate-400 font-medium">o</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          {/* form */}
          <form className="space-y-4" onSubmit={onSubmit}>

            {/* name */}
            <div>
              <label className="label" htmlFor="name">Nombre</label>
              <div className="relative">
                <User size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <input
                  id="name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  placeholder="Tu nombre"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="input pl-10"
                />
              </div>
            </div>

            {/* email */}
            <div>
              <label className="label" htmlFor="email">Email</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck={false}
                  required
                  placeholder="correo@ejemplo.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input pl-10"
                />
              </div>
            </div>

            {/* password */}
            <div>
              <label className="label" htmlFor="password">Contraseña</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  minLength={8}
                  maxLength={200}
                  // Safari reads this to generate a password that meets our rules.
                  {...{ passwordrules: 'minlength: 8; maxlength: 200; required: upper; required: lower;' }}
                  placeholder="••••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input pl-10 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>

              {/* password requirements */}
              {password.length > 0 && (
                <ul className="mt-2 space-y-1">
                  <PasswordRule met={hasLength}    text="Mínimo 8 caracteres" />
                  <PasswordRule met={hasUppercase} text="Al menos una mayúscula" />
                  <PasswordRule met={hasLowercase} text="Al menos una minúscula" />
                </ul>
              )}
              {password.length === 0 && (
                <ul className="mt-2 space-y-1">
                  <li className="flex items-center gap-1.5 text-xs text-slate-400"><span className="w-1 h-1 rounded-full bg-slate-300 inline-block" />Mínimo 8 caracteres</li>
                  <li className="flex items-center gap-1.5 text-xs text-slate-400"><span className="w-1 h-1 rounded-full bg-slate-300 inline-block" />Al menos una mayúscula</li>
                  <li className="flex items-center gap-1.5 text-xs text-slate-400"><span className="w-1 h-1 rounded-full bg-slate-300 inline-block" />Al menos una minúscula</li>
                </ul>
              )}
            </div>

            {/* timezone */}
            <div>
              <label className="label" htmlFor="timezone">Zona horaria</label>
              <div className="relative">
                <Globe size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <select
                  id="timezone"
                  name="timezone"
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  className="input pl-10"
                >
                  {timezoneOptions.map((tz) => (
                    <option key={tz.value} value={tz.value}>{tz.label}</option>
                  ))}
                </select>
              </div>
              <p className="mt-1 text-xs text-slate-400">
                Detectada automáticamente · puedes cambiarla
              </p>
            </div>

            {error && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting || !passwordValid}
              className="btn-primary w-full !py-3 !text-base mt-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Creando cuenta…' : 'Crear cuenta'}
            </button>
          </form>

          <p className="mt-6 text-sm text-slate-500 text-center">
            ¿Ya tienes cuenta?{' '}
            <Link to="/login" className="text-brand-600 font-medium hover:underline">
              Inicia sesión
            </Link>
          </p>
        </div>
      </div>
      </div>
    </div>
  );
}

function PasswordRule({ met, text }: { met: boolean; text: string }) {
  return (
    <li className={`flex items-center gap-1.5 text-xs transition-colors ${met ? 'text-emerald-600' : 'text-slate-400'}`}>
      {met
        ? <Check size={12} strokeWidth={2.5} className="text-emerald-500" />
        : <span className="w-1 h-1 rounded-full bg-slate-300 inline-block" />
      }
      {text}
    </li>
  );
}
